import axios from 'axios';
import { API_BASE } from './apiBase';

/*
 * Talks to the legacy Spring Boot server, NOT the retired FastAPI backend.
 *
 * Two changes from the original, both required by that server:
 *
 *  1. Base path is /uiapi, never /api. On the Spring server /api/** belongs to the
 *     worker fleet (httpBasic, CSRF disabled) and must not be disturbed.
 *
 *  2. Session cookies instead of JWT in localStorage. The server authenticates with a
 *     server-side session, so `withCredentials` is what carries identity. A token in
 *     localStorage is also readable by any XSS on the page; an HttpOnly session cookie
 *     is not, so this is a security improvement as well as an integration one.
 *
 * This frontend is deployed separately (e.g. app3.necromancerig.com) and calls the
 * backend cross-origin. Both must stay on the SAME registrable domain so the session
 * cookie remains "same-site" and survives without SameSite=None.
 *
 * REACT_APP_API_BASE examples:
 *   local dev  -> /uiapi            (relative: goes through src/setupProxy.js, no CORS involved)
 *   local dev  -> http://localhost:8080/uiapi   (absolute: the backend must allow this origin)
 *   production -> https://app.necromancerig.com/uiapi
 *
 * The default is RELATIVE on purpose. An absolute localhost default is only ever correct on one
 * machine: if a build ships without REACT_APP_API_BASE set, every deployed browser would try to
 * call the visitor's own localhost. "/uiapi" instead means same-origin, which is right for a
 * bundle served by Spring and right behind the dev proxy, and merely needs overriding when the
 * frontend is hosted separately from the API.
 */
// In DEVELOPMENT the base is ALWAYS the relative /uiapi, routed to the backend by
// src/setupProxy.js. Not the env var: the tooling that launches the dev server has been seen
// injecting a stale absolute REACT_APP_API_BASE (a dead localhost:8081) into its process
// environment, which beats every .env file and broke login for a whole session (2026-08-25).
// Relative-through-proxy also means no CORS and first-party cookies, whatever port dev lands on.
// Production builds honor REACT_APP_API_BASE as before.
// 2026-09-08: resolved through lib/apiBase.js, which VALIDATES the env var's shape instead of
// only defaulting it when absent. A Git Bash build turned REACT_APP_API_BASE=/uiapi into
// "C:/Program Files/Git/uiapi" (MSYS path conversion), that string became axios's baseURL, and
// Chrome refused every call as a file:// load - the whole app was unreachable behind its login.
const API_URL = API_BASE;

const api = axios.create({
  baseURL: API_URL,
  // Sends the JSESSIONID cookie cross-origin. Requires the server to reply with an
  // explicit Access-Control-Allow-Origin (never "*") plus Allow-Credentials: true.
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
  // Spring publishes CSRF as a JS-readable XSRF-TOKEN cookie; axios echoes it back on
  // state-changing requests. These are axios defaults, set explicitly so the contract
  // with UiApiSecurityConfig is visible here rather than implied.
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  // axios >=1.6 suppresses the XSRF header on CROSS-ORIGIN requests unless this is set.
  // This UI is deployed cross-origin from /uiapi (e.g. app3.necromancerig.com -> the API host,
  // and localhost:3000 -> localhost:8080/8081 in dev), so without this every POST/PUT/DELETE
  // reaches Spring with no X-XSRF-TOKEN and is rejected 403 even though the cookie is present.
  withXSRFToken: true,
});

/**
 * Primes the XSRF-TOKEN cookie. Call once at app start, before any POST/PUT/DELETE.
 * Safe to call repeatedly.
 */
export const initCsrf = () => api.get('/auth/csrf');

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 2026-09-08: every error toast in this app reads `data.detail`, which is FastAPI's shape -
    // the V2 backend. The Java backend answers with `message` on some routes and `detail` on
    // others (20 send detail, 8 send message). On the `message` ones the UI silently dropped the
    // server's explanation and showed its own generic fallback, so a device that could not be
    // deleted said "Failed to delete device" instead of "14 slots are assigned to device1; move
    // them to another device first". The reason was on the wire the whole time.
    //
    // Normalise HERE rather than at 35 call sites: fill `detail` from whatever the server did
    // send. Nothing that already works changes, because an existing `detail` is never touched.
    const body = error.response?.data;
    if (body && typeof body === 'object' && !body.detail) {
      const better = body.message || body.error_description || body.error;
      if (typeof better === 'string' && better.trim()) {
        body.detail = better;
      }
    }

    if (error.response?.status === 401) {
      // Nothing to clear: identity lives in an HttpOnly cookie the server controls.
      // Avoid bouncing when the failure IS the login attempt, or we mask the error.
      const isLoginCall = error.config?.url?.includes('/auth/login');
      // r16p review (2026-10-24): the chat READS (lib/chatsApi.js: info, summary, threads, thread) opt out. The summary is polled
      // every 60 s on EVERY page (Layout's Inbox pill), so a 401 that means "no chat access for this login" (an Inventory client
      // login, a session older than "Lock API access") would bounce the whole dashboard to /login - a sign-in loop when the server
      // always answers that login 401. Those callers treat 401 as "no inbox" instead; every other call still redirects.
      const quiet = !!error.config?.necroNoAuthRedirect;
      if (!isLoginCall && !quiet && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// ==================== Auth API ====================
export const authApi = {
  // Establishes a server-side session; the browser stores the cookie automatically.
  login: async (email, password) => {
    await initCsrf();
    return api.post('/auth/login', { email, password });
  },
  // Identity comes from /uiapi/me on the Spring server (not /auth/me).
  getMe: () => api.get('/me'),
  // r16pm review: ends the SERVER session (AuthContext.logout). A 401 here (the session is already gone) must not bounce to /login
  // on its own: the caller is on its way there.
  logout: () => api.post('/auth/logout', null, { necroNoAuthRedirect: true, timeout: 10000 }),

  // --- not available on the Spring backend ---------------------------------
  // Registration and password reset still live in the legacy Thymeleaf UI
  // (/registration, /login). Failing loudly beats silently pretending to work.
  signup: () => Promise.reject(new Error('Signup is not available in the new UI yet - use the legacy UI')),
  updateMe: () => Promise.reject(new Error('Profile update is not implemented on /uiapi yet')),
  // No refresh tokens: the session cookie is renewed by the server.
  refresh: () => Promise.reject(new Error('Not applicable - session cookie auth has no refresh token')),
  passwordReset: (email) => api.post('/auth/password-reset', { email }),
  updatePasswordRecovery: (accessToken, newPassword) => api.post('/auth/update-password-recovery', { 
    access_token: accessToken, 
    new_password: newPassword 
  }),
};

// ==================== Slots API ====================
/*
 * Per-slot targeting data - who a slot engages and where it finds them.
 *
 * Lives HERE, not inline in the page. SlotTargetDataPage used to declare its own fetch() client
 * against /api/slots/{id}/target-data - a route from the abandoned Python V2 port that this server
 * has never mapped and that setupProxy does not even forward. Because the page's load effect was
 * `if (targetData)`, the failed call left its hardcoded initial state on screen as though it were
 * the customer's data. Going through the shared client means one baseURL, one auth path, one CSRF
 * token, and a 404 that is impossible to mistake for data.
 *
 * `slotName` is the slot's name ("slot1"), which is what the route takes and what the page already
 * has in the URL.
 */
export const targetDataApi = {
  get: (slotName) => api.get(`/slots/${slotName}/target-data`),
  // Partial by design: only the keys sent are written, so the page can save a subset. The server
  // refuses any key that is not one of the 50 targeting fields rather than ignoring it.
  save: (slotName, body) => api.put(`/slots/${slotName}/target-data`, body),
  // Round 14: the targets-page settings the server defines on top of the 50 ("9. Pick targets in
  // order (one per session)", "10. Auto-remove not-found targets from the list") - id, wire key,
  // label and tooltip, only those whose server part is ON:
  //   GET /uiapi/target-data/metadata -> { settings: [{ id, wire, label, numberedLabel, tooltip, control }], hidden: [ids] }
  // 404 = a server without it (round 13): the page then draws none of them. lib/targetData.js.
  metadata: () => api.get('/target-data/metadata'),
};

export const slotsApi = {
  list: (params) => api.get('/slots/', { params }),
  get: (slotId) => api.get(`/slots/${slotId}`),
  create: (data) => api.post('/slots/', data),
  update: (slotId, data) => api.put(`/slots/${slotId}`, data),
  // The customer's own pause. Deliberately its own route rather than a field on update():
  // a generic slot PUT is what let V2's UI move an ADMIN capacity bit. This writes one field.
  setEnabled: (slotName, enabled) => api.put(`/slots/${slotName}/enabled`, { enabled }),
  delete: (slotId) => api.delete(`/slots/${slotId}`),
  getSettings: (slotId) => api.get(`/slots/${slotId}/settings`),
  updateSettings: (slotId, settings) => api.put(`/slots/${slotId}/settings`, settings),
};

// ==================== SMM Orders API ====================
// Backed by UiApiSmmOrdersController. `list` returns a BARE ARRAY (the page maps over it
// directly); `summary` returns an object with the totals, because the list is capped server-side
// and the page has to be honest about showing N of M.
export const smmOrdersApi = {
  list: (params) => api.get('/smm-orders', { params }),
  summary: () => api.get('/smm-orders/summary'),
  // DELETE, not the legacy GET link: a GET that destroys an order can be triggered by anything
  // that prefetches the URL. CSRF is active on /uiapi and axios echoes the XSRF-TOKEN cookie.
  remove: (orderId) => api.delete(`/smm-orders/${orderId}`),
};

// ==================== SMM Settings API ====================
// The API key is WRITE-ONLY: get() never returns it, only api_key_configured + a masked hint.
// Sending no smm_panel_api_key on save keeps the stored one.
export const smmSettingsApi = {
  get: () => api.get('/smm-settings'),
  save: (body) => api.put('/smm-settings', body),
  validateGrid: (grid) => api.post('/smm-settings/validate-grid', { smm_services_settings_grid: grid }),
};

// ==================== r16o3: SMM subscription orders (V1 /smmSubscriptionOrders/) ====================
// POST, not V1's GET links (a prefetched GET could pause an order); CSRF applies.
export const smmSubscriptionsApi = {
  list: () => api.get('/smm-subscription-orders'),
  pause: (orderId) => api.post(`/smm-subscription-orders/${orderId}/pause`),
  resume: (orderId) => api.post(`/smm-subscription-orders/${orderId}/resume`),
};

// ==================== r16o3: inbuilt scraping (V1 /orderManagement/accountSettings, its scraping block) ====================
// The row id goes as a query parameter: a username such as "the.rock" must reach the server whole.
export const inbuiltScrapingApi = {
  get: () => api.get('/inbuilt-scraping'),
  save: (body) => api.put('/inbuilt-scraping', body),
  removeActive: (id) => api.delete('/inbuilt-scraping/active', { params: { id } }),
};

// ==================== Devices API ====================
export const devicesApi = {
  list: (params) => api.get('/devices/', { params }),
  get: (deviceId) => api.get(`/devices/${deviceId}`),
  create: (data) => api.post('/devices/', data),
  update: (deviceId, data) => api.put(`/devices/${deviceId}`, data),
  delete: (deviceId) => api.delete(`/devices/${deviceId}`),
  // The customer's device-wide pause - one switch that stops every slot on this phone. Its own
  // route, like the slot pause, so a generic device PUT can never move it by accident.
  setEnabled: (deviceId, enabled) => api.put(`/devices/${deviceId}/enabled`, { enabled }),
  heartbeat: (deviceId) => api.post(`/devices/${deviceId}/heartbeat`),
};

export const phoneDriverTransitionsApi = {
  request: (deviceId, requestedShared) => api.post(`/devices/${encodeURIComponent(deviceId)}/phone-driver/transitions`, { requestedShared }),
  get: (operationId) => api.get(`/phone-driver/transitions/${encodeURIComponent(operationId)}`),
  retry: (operationId) => api.post(`/phone-driver/transitions/${encodeURIComponent(operationId)}/retry`),
  // Recovery contract (e): closes an unclaimed request, or a FAILED / EXPIRED job that never changed the phone
  // (200 CANCELLED, route unchanged); otherwise 409 PHONE_TRANSITION_NEEDS_VERIFICATION. Session + CSRF like every POST.
  cancel: (operationId) => api.post(`/phone-driver/transitions/${encodeURIComponent(operationId)}/cancel`),
};

// ==================== BM Presence API ====================
// Replaces V2's detectedDevicesApi (GET /detected-devices/). The Java stack exposes the same
// "which PCs are connected and which phones adb sees" surface over the BM uplink at /uiapi/bm.
//
//   GET  /uiapi/bm/presence
//        -> { clients:[ { installId, label, hostname, client_connected, lastSeen,
//                         devices:[ { serial, model, state } ] } ] }
//   POST /uiapi/bm/presence/{installId}/approve  { serial, label }
//        -> 201 { status:'approved', device:{ device_id, label, udid, device_name } }
//
// Field renames vs V2's /detected-devices/: pc_identifier -> hostname; device.status -> device.state
// (adb state 'device'|'unauthorized'|'offline'); installId is added (log-view + approve key).
// There is no server-side "dismiss" (V2's DELETE /detected-devices/{serial} has no Java equivalent);
// the Devices page hides dismissed/approved serials client-side. See approveFlow in the port notes.
export const bmPresenceApi = {
  list: () => api.get('/bm/presence'),
  // `deviceId` is optional: omitted, the server takes the lowest FREE deviceN. Passed, the
  // customer chose which slot in their allowance this phone occupies - a 409 {error:'device_limit'}
  // comes back if that id was taken between rendering the picker and pressing Add.
  approve: (installId, serial, label, deviceId) =>
    api.post(`/bm/presence/${installId}/approve`,
      deviceId ? { serial, label, device_id: deviceId } : { serial, label }),
  // C1: Stop / Restart one captured worker on that PC. Travels down the PC's own uplink socket;
  // the server waits up to 8 s for the PC's answer and returns it verbatim
  // ({ok, result}). 409 = PC not connected, 502 = PC refused (remoteControls=false there, unknown
  // id, kill failed), 504 = no answer in time.
  workerCommand: (installId, workerId, op) =>
    api.post(`/bm/presence/${installId}/workers/${encodeURIComponent(workerId)}/${op}`),
};

// ==================== Monitor video self-heal (docs/MONITOR-VIDEO-SELFHEAL-DESIGN.md S4/S5) ====================
//   GET  /uiapi/bm/video/config
//        -> { transport:'auto'|'remote'|'local', reconnect, states, repair, repairCooldownSec,
//             remoteLow?:{bitrate,maxFps,maxSize} }
//        404 = an older server: the Monitor behaves exactly as before (lib/monitorConfig.js).
//   POST /uiapi/bm/presence/{installId}/video/repair  { serials:[..], press:"p-.." }  (empty = every watched phone)
//        -> 200 {ok:true, code:"OK", result:"<the PC's own words>"}; 502 {ok:false, code:"CLIENT_TOO_OLD"
//           (launcher r3: "unknown op") | "REMOTE_CONTROLS_OFF" | "REFUSED", result}; 504 {code:"TIMEOUT"};
//           409 {code:"NOT_CONNECTED"}; 429 {code:"RATE_LIMITED", scope, retryAfterSec}; 400 {code:"BAD_SERIALS"};
//           404 not owned / repair switched off. `press` groups one Refresh press across PCs, so it counts
//        once against the per-user hourly limit. The server waits up to 30 s for the PC.
export const bmVideoApi = {
  config: (timeoutMs = 3000) => api.get('/bm/video/config', { timeout: timeoutMs }),
  repair: (installId, serials, press) =>
    api.post(`/bm/presence/${encodeURIComponent(installId)}/video/repair`,
      press ? { serials: Array.isArray(serials) ? serials : [], press } : { serials: Array.isArray(serials) ? serials : [] },
      { timeout: 40000 }),
};

// ==================== Settings write path (U4) ====================
// Everything here is gated server-side by the UIAPI_SETTINGS_WRITE feature flag (the super admin
// can kill it on the Flags page; every call then answers 403). Values are keyed by SETTING ID -
// the verified Java contract - never by V2's invented nested shapes.
//
//   GET  /uiapi/settings/write-info                          -> { enabled, sections, identityIds, historyRetention, maxTargets }
//   PUT  /uiapi/slots/{slot}/settings { values:{id:v}, allowIdentity?, dryRun?, reason? } -> { changed, diff, changeId }
//   GET  /uiapi/slots/{slot}/settings/history                -> { retention, changes:[{changeId, at, source, reason, count, values}] }
//   POST /uiapi/slots/{slot}/settings/revert/{changeId}      -> same shape as PUT (a revert is itself recorded)
//   POST /uiapi/slots/bulk-copy { source, targets, ids?|sections?, dryRun? } -> per-target diffs
export const settingsWriteApi = {
  info: () => api.get('/settings/write-info'),
  // The server's OWN id list. The bundled fieldMetadataComplete.js is a generated copy and has
  // drifted from it (measured 2026-08-27: they disagree on 2 ids in the 51-3000 range), and the
  // ids are SPARSE - 637 settings spread over 0..5002 - so "is 640 a real setting?" cannot be
  // answered by a range check. Anything validating typed setting numbers must ask the server.
  metadata: () => api.get('/settings/metadata'),
  put: (slotName, values, opts = {}) => api.put(`/slots/${encodeURIComponent(slotName)}/settings`, { values, ...opts }),
  history: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/settings/history`),
  revert: (slotName, changeId) => api.post(`/slots/${encodeURIComponent(slotName)}/settings/revert/${changeId}`),
  bulkCopy: (payload) => api.post('/slots/bulk-copy', payload),
};

// ==================== Round 14: slot stats + not-found targets ====================
// Coded against the round-14 server contract (branch r14/server). Every caller treats 404/405 as
// "this server does not have the route yet" and says so - never as an empty result.
//
//   GET  /uiapi/slots/{slot}/stats                       -> IGAccStats history fields (lib/slotHistory.js)
//   GET  /uiapi/slots/{slot}/targets/not-found           -> { autoRemove, targets:[{ target, misses,
//                                                             missDays[], skippedSince, removedFromList,
//                                                             type }] }
//   POST /uiapi/slots/{slot}/targets/not-found/restore { target[, type] } -> { restored, target }
//
// Round 14b (W11, contract C2): a row carries the list TYPE it was missed in (COMPETITORACCOUNTS,
// HASHTAGS, LOCATIONS, DIRECTACCOUNTS; missing on rows from the old 3-field report). Restore sends
// {target, type} for a typed row and {target} otherwise (= every type of that target). Which of the
// two the card sends is decided by the card (lib/r14Switch.js r14bUiEnabled); this only builds it.
//
// Round 15 (server branches r15/server @ 568a202 and r15c/server @ 41d81aa), each its own route because
// /stats answers 404 when both round-14 history parts are OFF; each answers 404 {error:'not_enabled'} while
// its server part is OFF (both OFF unless switched on), and a server before round 15 has no such route:
//   GET  /uiapi/slots/{slot}/close-friends     -> CloseFriendsStats.View (lib/closeFriendsView.js)
//   GET  /uiapi/slots/{slot}/saved-collection  -> SavedCollectionStats.View (lib/savedCollectionView.js)
export const slotStatsApi = {
  get: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/stats`),
  notFoundTargets: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/targets/not-found`),
  restoreNotFound: (slotName, target, type) =>
    api.post(
      `/slots/${encodeURIComponent(slotName)}/targets/not-found/restore`,
      type === undefined || type === null || String(type).trim() === '' ? { target } : { target, type },
    ),
  closeFriends: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/close-friends`),
  savedCollection: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/saved-collection`),
  // r16 TT-3 (2026-10-03): a TikTok slot's own counters (tiktok_stats) -> lib/tiktokStatsView.js; 404 on an older server or
  // while the server's TikTok stats are switched off; an Instagram slot answers applies:false.
  // r16 TT-7 (review round 1 should-fix): bounded by TIKTOK_STATS_TIMEOUT_MS - the Slot Stats page waits for this answer before it
  // draws the Instagram-only cards (Close Friends, the saved collection), and the shared axios client has no default timeout, so a
  // request that hung kept those cards hidden on an Instagram slot. A timeout is an error: the Instagram page is drawn as before.
  tiktok: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/tiktok-stats`, { timeout: TIKTOK_STATS_TIMEOUT_MS }),
  // r16m (design 3.6): the slot's follow-back attribution, counts only (lib/followBackView.js reads it). range = 7 | 30 | 90 | 'all'
  // (the matured cohort [asOf - window - range, asOf - window)); target = one source account to filter to. 404 on an older server,
  // 404 not_enabled while the server's follow-back part is off, 404 slot_not_found like /stats.
  followBack: (slotName, { range = 30, target } = {}) => api.get(`/slots/${encodeURIComponent(slotName)}/follow-back`, {
    params: target ? { range, target } : { range },
  }),
};

/*
 * r16m (design 3.6 / 2.4): the follow-back group page and the batch records.
 *   GET  /uiapi/follow-back?batch=<id>&range=..&target=..   or   ?slots=801-805,901-905&range=..   -> the same answer as the slot route
 *   GET  /uiapi/follow-back/batches                          -> the account's batch_group records (members, pending, devices, seeding)
 *   POST /uiapi/follow-back/batches/{id}/join   {slot}       PENDING / DETACHED slot -> member (its free phones are added)
 *   POST /uiapi/follow-back/batches/{id}/detach {slot}       member -> DETACHED (its ledger rows stay with the batch)
 *   POST /uiapi/follow-back/batches/{id}/retire              the batch ends; its phones are freed; its rows stay as history, and the
 *                                                            next batch on those phones copies every non-RELEASED row (as SEEDED)
 * Every route is scoped by the logged-in account on the server; a batch id or slot outside it is refused there.
 */
export const followBackApi = {
  group: ({ batch, slots, range = 30, target } = {}) => api.get('/follow-back', {
    params: {
      ...(batch !== undefined && batch !== null && batch !== '' ? { batch } : { slots }),
      range,
      ...(target ? { target } : {}),
    },
  }),
  batches: () => api.get('/follow-back/batches'),
  join: (batchId, slot) => api.post(`/follow-back/batches/${encodeURIComponent(batchId)}/join`, { slot }),
  detach: (batchId, slot) => api.post(`/follow-back/batches/${encodeURIComponent(batchId)}/detach`, { slot }),
  retire: (batchId) => api.post(`/follow-back/batches/${encodeURIComponent(batchId)}/retire`),
};

/** r16 TT-7: how long the TikTok stats read may take before the Slot Stats page treats it as failed (the Instagram page). */
export const TIKTOK_STATS_TIMEOUT_MS = 8000;

// ==================== Support tickets (A1/A2) ====================
// Server-side: ROLE_ADMIN + the UIAPI_SUPPORT_UI flag on every endpoint (info answers without the
// flag so the page can say WHY it is off). Detail responses carry tenant data - admin eyes only.
export const supportApi = {
  info: () => api.get('/support/info'),
  queue: (params = {}) => api.get('/support/tickets', { params }),
  detail: (id) => api.get(`/support/tickets/${encodeURIComponent(id)}`),
  setStatus: (id, body) => api.post(`/support/tickets/${encodeURIComponent(id)}/status`, body),
  runVerification: () => api.post('/support/verification/run'),
  // A6: a question and a bug arrive looking identical - re-route in place, or merge.
  convert: (id, body) => api.post(`/support/tickets/${encodeURIComponent(id)}/convert`, body),
};

// ==================== Feedback loop / learned KB (A6, staff) ====================
// Two artifacts, two gate reports, two approvals. The reply endpoint is the ONLY way a
// knowledge-gap ticket becomes "answered" - it runs the gates and delivers into the askers' chats.
export const kbApi = {
  review: (ticketId) => api.get(`/kb/review/${encodeURIComponent(ticketId)}`),
  check: (ticketId, text) => api.post(`/kb/review/${encodeURIComponent(ticketId)}/check`, { text }),
  reply: (ticketId, text) => api.post(`/kb/review/${encodeURIComponent(ticketId)}/reply`, { text }),
  entries: (params = {}) => api.get('/kb/entries', { params }),
  seed: (body) => api.post('/kb/entries', body),
  promote: (messageId) => api.post('/kb/entries/promote', { messageId }),
  checkEntry: (id, body) => api.post(`/kb/entries/${encodeURIComponent(id)}/check`, body),
  approve: (id, body) => api.post(`/kb/entries/${encodeURIComponent(id)}/approve`, body),
  reject: (id) => api.post(`/kb/entries/${encodeURIComponent(id)}/reject`),
  flag: (id, reason) => api.post(`/kb/entries/${encodeURIComponent(id)}/flag`, { reason }),
  confirm: (id) => api.post(`/kb/entries/${encodeURIComponent(id)}/confirm`),
  retire: (id) => api.post(`/kb/entries/${encodeURIComponent(id)}/retire`),
  metrics: () => api.get('/kb/metrics'),
  deliverPending: () => api.post('/kb/deliver-pending'),
};

// ==================== Customer assistant (A5a) ====================
// Server-side: authenticated + the UIAPI_ASSISTANT flag; every read is scoped to the logged-in
// account. The assistant EXPLAINS (V0 tier) - it has no write tools at all.
export const assistantApi = {
  info: () => api.get('/assistant/info'),
  chat: (body) => api.post('/assistant/chat', body),
  chats: () => api.get('/assistant/chats'),
  chatMessages: (id) => api.get(`/assistant/chats/${id}`),
  report: (chatId) => api.post(`/assistant/report/${chatId}`),
  // A6: "Did this resolve it?" - always answers {ok:true}; a poor verdict escalates server-side.
  rate: (messageId, body) => api.post(`/assistant/rate/${messageId}`, body),
};

// ==================== r16sd: the Settings Doctor (D:/NecroWork/merge-r16sd/DOCTOR-API.md 2) ====================
//   GET /uiapi/slots/{slot}/doctor   -> { slot, doctor, holds[], autoHandled[], findings[], dismissed[], computedAt } (own slot; 404
//                                        slot_not_found otherwise; a server without the doctor: 404 with no code)
//   GET /uiapi/admin/doctor/summary  -> ROLE_ADMIN only (403 otherwise): counts per rule key and account (user ids, no emails)
//   POST /uiapi/slots/{slot}/doctor/fix {rule, fix}   (stage 5, DOCTOR-API 11.2) -> {ok, rule, fix, changeId, changed, diff, blocked,
//                                        notes, findings}; the SERVER derives the values (none are sent); 404 fix_not_offered,
//                                        409 needs_support / save_refused, 403 settings_write_off, 503 doctor_off
//   POST /uiapi/slots/{slot}/doctor/dismiss {rule}    (stage 5, 11.3) -> {ok, rule, dismissed[], openFindings}; 404 nothing_to_dismiss
// The slot list's holds / attention and /uiapi/me's credits / doctor ride on the existing routes (slotsApi.list, authApi.getMe).
export const doctorApi = {
  panel: (slotName) => api.get(`/slots/${encodeURIComponent(slotName)}/doctor`),
  fix: (slotName, rule, fix) => api.post(`/slots/${encodeURIComponent(slotName)}/doctor/fix`, { rule, fix }),
  dismiss: (slotName, rule) => api.post(`/slots/${encodeURIComponent(slotName)}/doctor/dismiss`, { rule }),
  adminSummary: () => api.get('/admin/doctor/summary'),
};

// ==================== Readiness / gap engine (A4) ====================
// Deterministic goal checks: requirements.json x (slot settings + newest device snapshot).
// Tenant-scoped server-side; behind the UIAPI_READINESS flag (403 names the flag when off).
export const readinessApi = {
  goals: () => api.get('/readiness/goals'),
  evaluate: (slotName, goal) =>
    api.get(`/slots/${encodeURIComponent(slotName)}/readiness`, { params: { goal } }),
};

// Templates (U4c) - V2's "presets" rebuilt server-side: identity fields (IG username/password,
// device, label) are STRIPPED at save time, and applying goes through the same engine as every
// other write (preview diff first, snapshot + undo after).
export const templatesApi = {
  list: () => api.get('/templates'),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  remove: (id) => api.delete(`/templates/${id}`),
  apply: (id, slots, dryRun = false) => api.post(`/templates/${id}/apply`, { slots, dryRun }),
};

// ==================== Feature flags (Phase 0b) ====================
// Flags are REGISTERED IN CODE (a Java enum); the server only stores their state, so there is no
// "create flag" here - the admin page lists the registry and edits rows. A missing row = OFF.
//
//   GET    /uiapi/features/my                      -> { features: {KEY: bool}, source_details: {KEY: {...}} }
//   PUT    /uiapi/features/my/{key} { enabled, reason } -> user opt-in/out (UI-class flags only)
//   DELETE /uiapi/features/my/{key}                -> drop the user's override
//   GET    /uiapi/admin/features                   -> { flags: [...], registry_size, cache_ttl_ms }   (ROLE_ADMIN)
//   POST   /uiapi/admin/features/{key} { enabled_globally?, rollout_percent?, min_worker_version?, sunset_date?, notes? }
//   POST   /uiapi/admin/features/{key}/kill | /revive | /clearOverrides
//   DELETE /uiapi/admin/features/{key}             -> delete the row (back to unconfigured = OFF)
export const featureFlagsApi = {
  my: () => api.get('/features/my'),
  setMine: (key, enabled, reason) => api.put(`/features/my/${key}`, { enabled, reason }),
  clearMine: (key) => api.delete(`/features/my/${key}`),
  adminList: () => api.get('/admin/features'),
  adminGet: (key) => api.get(`/admin/features/${key}`),
  adminUpdate: (key, patch) => api.post(`/admin/features/${key}`, patch),
  adminKill: (key) => api.post(`/admin/features/${key}/kill`),
  adminRevive: (key) => api.post(`/admin/features/${key}/revive`),
  adminClearOverrides: (key) => api.post(`/admin/features/${key}/clearOverrides`),
  adminDelete: (key) => api.delete(`/admin/features/${key}`),
};

// ==================== BM Installs (desktop clients) API ====================
// How a customer gets a PC to appear at all: issue a per-install uplink token, paste it into the
// desktop client's token file, and the client's WebSocket uplink registers as connected.
//
//   POST   /uiapi/bm/installs   { label }  -> 201 { id, label, createdAt, token, wsPath, note }
//   GET    /uiapi/bm/installs             -> [ { id, label, createdAt, lastSeenAt, online, viewers } ]
//   DELETE /uiapi/bm/installs/{id}        -> 204 | 404
//
// The `token` is returned ONLY by POST (the server stores just its SHA-256), so the UI must show it
// once with copy-to-clipboard. The desktop client (BM) reads it from data/uplinkToken.txt.
export const bmInstallsApi = {
  list: () => api.get('/bm/installs'),
  create: (label) => api.post('/bm/installs', { label }),
  revoke: (id) => api.delete(`/bm/installs/${id}`),
};

// Where the desktop client (BM) expects its uplink token, shown in the "Add desktop client" dialog.
export const BM_TOKEN_FILE_PATH = 'data/uplinkToken.txt';

/**
 * Absolute URL for the live SSE log stream of an install, for use with the browser EventSource.
 * Replaces V2's raw WebSocket at /api/ws/logs/{serial}. Auth is the session cookie (EventSource is
 * opened withCredentials), so there is NO ?token= query. Keyed by installId, not device serial; the
 * optional deviceId is echoed back by the server in its first `status` frame for labelling only.
 *
 * GET /uiapi/bm/view/{installId}?deviceId=... (text/event-stream)
 *   event 'status' data { uplink:'online'|'offline'|'gone', viewers, lastSeq?, device?, ... }
 *   event 'log' id=<seq> data { seq, ts(epochMs), line }
 *   event 'gap' data { dropped }
 *   comment ':keepalive'
 */
export const bmLogViewUrl = (installId, deviceId) => {
  const raw = `${API_URL.replace(/\/+$/, '')}/bm/view/${encodeURIComponent(installId)}`;
  const absolute = /^https?:\/\//i.test(raw)
    ? raw
    : `${window.location.origin}${raw.startsWith('/') ? '' : '/'}${raw}`;
  const url = new URL(absolute);
  if (deviceId) {
    url.searchParams.set('deviceId', deviceId);
  }
  return url.toString();
};

// ==================== Sessions API ====================
export const sessionsApi = {
  list: (params) => api.get('/sessions/', { params }),
  get: (sessionId) => api.get(`/sessions/${sessionId}`),
  getActive: () => api.get('/sessions/active'),
  start: (slotId, metadata) => api.post('/sessions/', { slot_id: slotId, metadata }),
  updateStats: (sessionId, stats) => api.put(`/sessions/${sessionId}/stats`, stats),
  end: (sessionId, status, finalStats, errorMessage) => api.put(`/sessions/${sessionId}/end`, {
    status,
    final_stats: finalStats,
    error_message: errorMessage
  }),
  pause: (sessionId) => api.post(`/sessions/${sessionId}/pause`),
  resume: (sessionId) => api.post(`/sessions/${sessionId}/resume`),
};

// ==================== Logs API ====================
export const logsApi = {
  list: (params) => api.get('/logs/', { params }),
  create: (data) => api.post('/logs/', data),
  createBatch: (logs) => api.post('/logs/batch', { logs }),
  getErrors: (params) => api.get('/logs/errors', { params }),
  getStats: (hours) => api.get('/logs/stats', { params: { hours } }),
};

// ==================== Stats API ====================
export const statsApi = {
  getDaily: (params) => api.get('/stats/daily', { params }),
  getSlotStats: (slotId, days) => api.get(`/stats/slot/${slotId}`, { params: { days } }),
  getOverview: (days) => api.get('/stats/overview', { params: { days } }),
  getLeaderboard: (metric, days, limit) => api.get('/stats/leaderboard', { params: { metric, days, limit } }),
};

// ==================== Admin: user management (U5) ====================
// Backs /admin/users. Mirrors the legacy /userDataManagement/ page: the server delegates to the
// same UserService the Thymeleaf page calls, so slot creation, the plan ladder and the role rules
// have one implementation. Gated server-side by UIAPI_USER_ADMIN (ROLE_ADMIN + flag); a 403 whose
// error names the flag means "turn it on", a 403 "super admin only" means the legacy owner rule.
//
//   GET  /uiapi/admin/users             -> { users:[...], total, super_admin, roles, toggles }
//   GET  /uiapi/admin/users/{id}
//   POST /uiapi/admin/users             { first_name, last_name, email, password, role?, account_active?, slots, devices? } -> 201
//   POST /uiapi/admin/users/{id}        { first_name?, last_name?, password?, role?, account_active?, slots?, devices? }
//   POST /uiapi/admin/users/{id}/toggle { feature, enabled }   feature = one of the list's `toggles`
//
// No delete - the legacy page has it commented out, and it would cascade to every slot and order.
export const usersAdminApi = {
  list: () => api.get('/admin/users'),
  get: (id) => api.get(`/admin/users/${id}`),
  create: (body) => api.post('/admin/users', body),
  update: (id, body) => api.post(`/admin/users/${id}`, body),
  toggle: (id, feature, enabled) => api.post(`/admin/users/${id}/toggle`, { feature, enabled }),
};

// ==================== Admin API ====================
export const adminApi = {
  // Users: see usersAdminApi above (the V2 routes that used to be listed here never existed on
  // this server).

  // Stats
  getStats: () => api.get('/admin/stats'),
  getUserStats: (userId) => api.get(`/admin/stats/user/${userId}`),
  
  // All data views
  getAllSlots: (params) => api.get('/admin/all-slots', { params }),
  getAllDevices: (params) => api.get('/admin/all-devices', { params }),
};

// ==================== Client Release API ====================
export const clientApi = {
  // Channels
  getChannels: () => api.get('/client/channels'),
  getVersion: (channel = 'production') => api.get('/client/version', { params: { channel } }),
  getDownloadStatus: (channel = 'production') => api.get('/client/download-status', { params: { channel } }),
  checkUpdate: (currentVersion, channel = 'production') => api.get('/client/check-update', { 
    params: { current_version: currentVersion, channel } 
  }),
  
  // Downloads
  getDownloadUrl: (channel = 'production') => `${API_URL}/client/download?channel=${channel}`,
  
  // History
  getHistory: (channel = null, limit = 20) => {
    const params = { limit };
    if (channel) params.channel = channel;
    return api.get('/client/history', { params });
  },
  getHistoricalDownloadUrl: (channel, version) => `${API_URL}/client/history/download/${channel}/${version}`,
  
  // Admin only
  uploadRelease: (channel, version, releaseNotes, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/client/release?channel=${channel}&version=${version}&release_notes=${encodeURIComponent(releaseNotes)}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateChannel: (channel, data) => api.put(`/client/channel/${channel}`, null, { params: data }),
  deleteVersion: (channel, version) => api.delete(`/client/history/${channel}/${version}`),
  triggerCleanup: () => api.post('/client/cleanup'),
};

// ==================== WebSocket Helpers ====================
export const getWebSocketUrl = (channel, params = {}) => {
  const baseUrl = API_URL.replace('http', 'ws').replace('/api', '');
  const queryParams = new URLSearchParams({
    token: localStorage.getItem('accessToken'),
    ...params
  }).toString();
  
  return `${baseUrl}/api/ws/${channel}?${queryParams}`;
};

export const connectWebSocket = (channel, params = {}) => {
  const url = getWebSocketUrl(channel, params);
  const ws = new WebSocket(url);
  
  ws.onopen = () => {
    console.log(`WebSocket connected to ${channel}`);
  };
  
  ws.onerror = (error) => {
    console.error(`WebSocket error on ${channel}:`, error);
  };
  
  ws.onclose = (event) => {
    console.log(`WebSocket closed on ${channel}:`, event.code, event.reason);
  };
  
  return ws;
};

// ==================== Legacy API compatibility ====================
// Map old adminApi calls to new structure
export const legacyAdminApi = {
  getDashboardStats: () => statsApi.getOverview(7),
  getDashboardSlots: () => slotsApi.list(),
  getSlots: () => slotsApi.list(),
  getSlot: (slotId) => slotsApi.get(slotId),
  createSlot: (data) => slotsApi.create(data),
  updateSlot: (slotId, data) => slotsApi.update(slotId, data),
  deleteSlot: (slotId) => slotsApi.delete(slotId),
  getDevices: () => devicesApi.list(),
  createDevice: (data) => devicesApi.create(data),
  updateDevice: (deviceId, data) => devicesApi.update(deviceId, data),
  deleteDevice: (deviceId) => devicesApi.delete(deviceId),
  getSessions: (params) => sessionsApi.list(params),
  getDailyStats: (days) => statsApi.getDaily({ days }),
  getLogs: (params) => logsApi.list(params),
  getProfile: () => authApi.getMe(),
  updateProfile: (data) => authApi.updateMe(data),
  getSlotSettings: (slotId) => slotsApi.getSettings(slotId),
  updateSlotSettings: (slotId, settings) => slotsApi.updateSettings(slotId, settings),
};

// ==================== Stub APIs for pages not yet migrated ====================
// These are placeholder implementations
export const sheetsApi = {
  getStatus: () => Promise.resolve({ data: { connected: false } }),
  startLogin: () => Promise.resolve({ data: {} }),
  disconnect: () => Promise.resolve({ data: {} }),
  getConfigs: () => Promise.resolve({ data: [] }),
  createConfig: (data) => Promise.resolve({ data }),
  deleteConfig: (configId) => Promise.resolve({ data: {} }),
  exportStats: (data) => Promise.resolve({ data: {} }),
  importAccounts: (data) => Promise.resolve({ data: {} }),
  readSheet: () => Promise.resolve({ data: [] }),
  writeSheet: () => Promise.resolve({ data: {} }),
};

export const campaignsApi = {
  getCampaigns: (params) => Promise.resolve({ data: [] }),
  getCampaign: (campaignId) => Promise.resolve({ data: null }),
  createCampaign: (data) => Promise.resolve({ data }),
  updateCampaign: (campaignId, data) => Promise.resolve({ data }),
  deleteCampaign: (campaignId) => Promise.resolve({ data: {} }),
  getNextTarget: (slotName) => Promise.resolve({ data: null }),
  reportResult: () => Promise.resolve({ data: {} }),
};

export const repostApi = {
  getSettings: (slotName) => Promise.resolve({ data: {} }),
  updateSettings: (slotName, data) => Promise.resolve({ data }),
  getQueue: (params) => Promise.resolve({ data: [] }),
  addToQueue: (formData) => Promise.resolve({ data: {} }),
  removeFromQueue: (postId) => Promise.resolve({ data: {} }),
  getNextPost: (slotName) => Promise.resolve({ data: null }),
  reportResult: () => Promise.resolve({ data: {} }),
};

export const profilesApi = {
  getProfiles: (profileType) => Promise.resolve({ data: [] }),
  getProfile: (profileId) => Promise.resolve({ data: null }),
  createProfile: (data) => Promise.resolve({ data }),
  updateProfile: (profileId, data) => Promise.resolve({ data }),
  deleteProfile: (profileId) => Promise.resolve({ data: {} }),
  assignToDevice: () => Promise.resolve({ data: {} }),
  unassignFromDevice: () => Promise.resolve({ data: {} }),
  getDeviceProfile: () => Promise.resolve({ data: null }),
};

export default api;
